import type { LogoGridBlockContent } from "../types"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

interface LogoGridBlockEditorProps {
  content: LogoGridBlockContent | null
  onChange: (content: LogoGridBlockContent) => void
}

export function LogoGridBlockEditor({ content, onChange }: LogoGridBlockEditorProps) {
  const c = content ?? {}
  const logos = c.logos ?? [{ name: "", image_url: "", url: "" }]
  const update = (partial: Partial<LogoGridBlockContent>) =>
    onChange({ ...c, ...partial })
  const setLogo = (
    i: number,
    field: keyof NonNullable<LogoGridBlockContent["logos"]>[number],
    val: string
  ) => {
    const next = [...logos]
    if (!next[i]) next[i] = { name: "", image_url: "", url: "" }
    next[i] = { ...next[i], [field]: val }
    update({ logos: next })
  }
  const addLogo = () =>
    update({ logos: [...logos, { name: "", image_url: "", url: "" }] })
  const removeLogo = (i: number) =>
    update({ logos: logos.filter((_, j) => j !== i) })

  return (
    <Card>
      <CardHeader>
        <h3 className="text-base font-medium">Logo Grid</h3>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Heading</Label>
          <Input
            value={c.heading ?? ""}
            onChange={(e) => update({ heading: e.target.value })}
            placeholder="Our partners"
          />
        </div>
        <div>
          <Label>Subheading</Label>
          <Input
            value={c.subheading ?? ""}
            onChange={(e) => update({ subheading: e.target.value })}
            placeholder="Optional subheading"
          />
        </div>
        <div>
          <Label>Columns</Label>
          <Select
            value={String(c.columns ?? 4)}
            onValueChange={(v) => update({ columns: Number(v) as 2 | 3 | 4 | 5 })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2">2</SelectItem>
              <SelectItem value="3">3</SelectItem>
              <SelectItem value="4">4</SelectItem>
              <SelectItem value="5">5</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Logo colors</Label>
          {/* NOS-238: override for the white-logo dark treatment. Auto darkens
              the row only when the import flagged white/light artwork. */}
          <Select
            value={c.logo_treatment ?? "auto"}
            onValueChange={(v) =>
              update({
                logo_treatment: v === "dark" || v === "original" ? v : undefined,
              })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Auto (darken white logos)</SelectItem>
              <SelectItem value="dark">Always dark</SelectItem>
              <SelectItem value="original">Original colors</SelectItem>
            </SelectContent>
          </Select>
          {(c.logos ?? []).some((l) => l?.light === true) && (
            <p className="text-xs text-muted-foreground mt-1">
              Some of these logos are white artwork from your old site — with
              original colors they may be invisible on light backgrounds.
            </p>
          )}
        </div>
        <div className="space-y-3">
          <Label>Logos</Label>
          {logos.map((logo, i) => (
            <div key={i} className="rounded-md border p-3 space-y-2">
              <Input
                placeholder="Name"
                value={logo.name ?? ""}
                onChange={(e) => setLogo(i, "name", e.target.value)}
              />
              <Input
                placeholder="Image URL"
                value={logo.image_url ?? ""}
                onChange={(e) => setLogo(i, "image_url", e.target.value)}
              />
              <Input
                placeholder="Link URL (optional)"
                value={logo.url ?? ""}
                onChange={(e) => setLogo(i, "url", e.target.value)}
              />
              <Button type="button" variant="ghost" size="sm" onClick={() => removeLogo(i)}>
                Remove
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={addLogo}>
            Add logo
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
